<template>
  <div
    ref="containerRef"
    :class="[
      'select-v2 tw:relative tw:inline-flex tw:items-center tw:w-full tw:rounded-[8px] tw:border tw:transition-all tw:duration-150 tw:cursor-pointer',
      sizeClass,
      stateClasses,
      { 'skeleton-play': skeleton },
    ]"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
    @click="toggle"
  >
    <!-- Selected value or placeholder -->
    <div class="tw:flex-1 tw:min-w-0 tw:truncate">
      <span v-if="selectedLabel" class="tw:text-(--fontcolor)">{{ selectedLabel }}</span>
      <span v-else class="tw:text-(--placeholder-color)">{{ resolvedPlaceholder }}</span>
    </div>

    <!-- Chevron -->
    <IconGoogle
      :name="isOpen ? 'expand_less' : 'expand_more'"
      size="1.25rem"
      color="var(--text-color)"
      class="tw:shrink-0 tw:transition-transform tw:duration-200"
    />

    <!-- Dropdown -->
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="dropdownRef"
        class="tw:fixed tw:bg-(--bg-input) tw:border tw:border-(--input-border-color) tw:rounded-lg tw:shadow-lg tw:overflow-hidden tw:z-[200000]"
        :style="dropdownStyle"
      >
        <!-- Search -->
        <div v-if="searchable" class="tw:p-2 tw:border-b tw:border-(--divider-color)">
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="tw:w-full tw:bg-transparent tw:border-none tw:outline-none tw:text-sm tw:text-(--fontcolor) tw:placeholder-(--placeholder-color)"
            :placeholder="resolvedSearchPlaceholder"
          />
        </div>

        <!-- Options list -->
        <div class="tw:overflow-y-auto tw:max-h-[240px]">
          <div
            v-if="filteredOptions.length === 0"
            class="tw:px-3 tw:py-2 tw:text-sm tw:text-(--text-color)"
          >
            {{ resolvedNoOptionsText }}
          </div>
          <div
            v-for="(option, index) in filteredOptions"
            :key="getOptionKey(option, index)"
            :class="[
              'tw:px-3 tw:py-2 tw:text-sm tw:cursor-pointer tw:transition-colors tw:duration-100',
              isSelected(option)
                ? 'tw:bg-black/10 dark:tw:bg-white/10 tw:text-(--fontcolor) tw:font-semibold'
                : 'tw:text-(--fontcolor) hover:tw:bg-black/5 dark:hover:tw:bg-white/10',
            ]"
            @click.stop="selectOption(option)"
          >
            {{ getOptionLabel(option) }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import IconGoogle from '../../atoms/IconGoogle/IconGoogle.vue'

export interface SelectOption {
  [key: string]: unknown
}

const { t } = useI18n()

const props = withDefaults(defineProps<{
  modelValue?: string | number | null
  options?: SelectOption[] | string[]
  placeholder?: string
  name?: string
  label?: string
  trackBy?: string
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  readonly?: boolean
  skeleton?: boolean
  error?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  noOptionsText?: string
}>(), {
  modelValue: null,
  options: () => [],
  placeholder: undefined,
  name: undefined,
  label: 'label',
  trackBy: 'id',
  size: 'md',
  disabled: false,
  readonly: false,
  skeleton: false,
  error: false,
  searchable: false,
  searchPlaceholder: undefined,
  noOptionsText: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null]
  'select': [option: SelectOption | string | null]
}>()

// i18n defaults
const resolvedPlaceholder = computed(() => props.placeholder ?? t('multiselect.placeholder'))
const resolvedSearchPlaceholder = computed(() => props.searchPlaceholder ?? t('multiselect.search'))
const resolvedNoOptionsText = computed(() => props.noOptionsText ?? t('multiselect.no_options'))

const containerRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)
const isOpen = ref(false)
const hovered = ref(false)
const focused = ref(false)
const searchQuery = ref('')
const dropdownStyle = ref<Record<string, string>>({})

// --- Size classes (matching InputPlayV3) ---
const sizeClass = computed(() => ({
  sm: 'tw:h-8 tw:px-2 tw:text-xs',
  md: 'tw:h-[46px] tw:px-3 tw:text-sm',
  lg: 'tw:h-[52px] tw:px-4 tw:text-base',
}[props.size]))

// --- State classes ---
const stateClasses = computed(() => {
  if (props.disabled) return 'tw:border-(--input-border-color) tw:bg-(--bg-input) tw:opacity-60 tw:cursor-not-allowed'
  if (props.error) return 'tw:border-(--feedback-error-500) tw:bg-(--bg-input)'
  if (focused.value || isOpen.value) return 'tw:border-(--fontcolor) tw:bg-(--bg-input) tw:ring-2 tw:ring-(--fontcolor)/10'
  if (hovered.value) return 'tw:border-(--fontcolor) tw:bg-(--bg-input)'
  return 'tw:border-(--input-border-color) tw:bg-(--bg-input)'
})

// --- Option helpers ---
function isObjectOption(opt: unknown): opt is SelectOption {
  return typeof opt === 'object' && opt !== null
}

function getOptionKey(option: SelectOption | string, index: number): string | number {
  if (isObjectOption(option)) return (option[props.trackBy] as string | number) ?? index
  return index
}

function getOptionLabel(option: SelectOption | string): string {
  if (isObjectOption(option)) return String(option[props.label] ?? '')
  return String(option)
}

function getOptionValue(option: SelectOption | string): string | number {
  if (isObjectOption(option)) return option[props.trackBy] as string | number
  return option as string
}

function isSelected(option: SelectOption | string): boolean {
  return getOptionValue(option) === props.modelValue
}

// --- Selected label ---
const selectedLabel = computed(() => {
  if (props.modelValue == null) return ''
  const found = props.options.find(opt => getOptionValue(opt) === props.modelValue)
  return found ? getOptionLabel(found) : ''
})

// --- Filtered options ---
const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options
  const q = searchQuery.value.toLowerCase()
  return props.options.filter(opt => getOptionLabel(opt).toLowerCase().includes(q))
})

// --- Toggle / select ---
function toggle() {
  if (props.disabled || props.readonly || props.skeleton) return
  if (isOpen.value) {
    close()
  } else {
    open()
  }
}

function open() {
  isOpen.value = true
  focused.value = true
  searchQuery.value = ''
  nextTick(() => {
    positionDropdown()
    if (props.searchable) searchInputRef.value?.focus()
    // Delay attaching outside click to avoid the same click event closing the dropdown
    requestAnimationFrame(() => {
      document.addEventListener('mousedown', handleOutsideClick)
    })
  })
}

function close() {
  isOpen.value = false
  focused.value = false
  document.removeEventListener('mousedown', handleOutsideClick)
}

function handleOutsideClick(e: MouseEvent) {
  const target = e.target as Node
  if (
    containerRef.value?.contains(target) ||
    dropdownRef.value?.contains(target)
  ) return
  close()
}

function selectOption(option: SelectOption | string) {
  const value = getOptionValue(option)
  emit('update:modelValue', value)
  emit('select', option)
  close()
}

// --- Positioning ---
function positionDropdown() {
  if (!containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  const gap = 4
  const spaceBelow = window.innerHeight - rect.bottom
  const openUp = spaceBelow < 260 && rect.top > 260

  dropdownStyle.value = {
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    ...(openUp
      ? { bottom: `${window.innerHeight - rect.top + gap}px`, top: 'auto' }
      : { top: `${rect.bottom + gap}px`, bottom: 'auto' }
    ),
  }
}

// Reposition on scroll/resize while open
function onScrollResize() {
  if (isOpen.value) positionDropdown()
}

watch(isOpen, (open) => {
  if (open) {
    window.addEventListener('scroll', onScrollResize, true)
    window.addEventListener('resize', onScrollResize)
  } else {
    window.removeEventListener('scroll', onScrollResize, true)
    window.removeEventListener('resize', onScrollResize)
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScrollResize, true)
  window.removeEventListener('resize', onScrollResize)
  document.removeEventListener('mousedown', handleOutsideClick)
})
</script>

<style scoped>
.select-v2.skeleton-play > * {
  opacity: 0;
}
</style>
